import { ThemeCompany } from '@api/api/theme-overview';
import { ButtonLink } from '@design/components/button-link/ButtonLink';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { config } from '@terminal/config/app.config';
import { WebOnlyFeat } from '@terminal/config/WebOnlyFeat';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys } from '@terminal/routes/query-params';
import { PosthogTrackingSource } from '@terminal/services/tracking/posthog/constants';
import { DrawerSectionHeader } from '@terminal/views/theme-overview/theme-overview-drawer/components/drawer-section-header/DrawerSectionHeader';
import { THEME_TAB_COMPANIES } from '@terminal/views/theme-overview/theme-overview-header/ThemeOverviewHeader';
import { useTranslation } from 'react-i18next';

import { CompanyItem } from './company-item/CompanyItem';
import styles from './TopCompaniesSection.module.scss';

interface TopCompaniesSectionProps {
  companies: ThemeCompany[];
  remainingCount: number;
  source: PosthogTrackingSource;
  tooltipThemeId?: string;
}

export const TopCompaniesSection = ({
  companies,
  remainingCount,
  source,
  tooltipThemeId,
}: TopCompaniesSectionProps) => {
  const { t } = useTranslation(['widget']);
  const { params } = useQueryParams();
  const { navigateToPage } = useNavigateToAssetOverview();
  const { navigateToTheme } = useNavigateToThemeOverview();

  const openAssetOverview = (value: string) => {
    navigateToPage({ entityTag: value, source });
  };

  const openThemeOverview = () => {
    const themeId = params[queryKeys.themeId] || tooltipThemeId;
    const newSearchParams = new URLSearchParams();
    newSearchParams.set(queryKeys.activeSubTab, THEME_TAB_COMPANIES);
    navigateToTheme({
      themeId: themeId || '',
      source,
      searchParams: newSearchParams,
    });
  };

  return (
    <ErrorBoundaryWrapper>
      <div className={styles.root}>
        <div className={styles.wrapper}>
          <DrawerSectionHeader
            className={styles.sectionHeader}
            title={t('themeOverview.topCompaniesTitle', {
              total: companies.length,
            })}
            description={t('themeOverview.rankedBySignificance')}
          />
          <ErrorBoundaryWrapper>
            <>
              <div className={styles.gridContainer}>
                {companies.map(company => (
                  <div
                    className={styles.companyItemWrapper}
                    key={company.company}
                  >
                    <CompanyItem
                      company={company}
                      onPillClick={
                        !config.app.isTeamsApp
                          ? () => openAssetOverview(company.company)
                          : () => void 0
                      }
                      iconSize={20}
                    />
                  </div>
                ))}
              </div>
              {!!remainingCount && (
                <WebOnlyFeat>
                  <ButtonLink
                    className={styles.styledButtonLink}
                    label={t('widget:themeOverview.viewMoreCompanies', {
                      total: remainingCount,
                    })}
                    iconName="ChevronLightRight"
                    iconPosition="right"
                    onClick={() => openThemeOverview()}
                  />
                </WebOnlyFeat>
              )}
            </>
          </ErrorBoundaryWrapper>
        </div>
      </div>
    </ErrorBoundaryWrapper>
  );
};
